import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { getEcho } from 'lib/echo';
import { useAuthStore } from 'store/authStore';
import { notificationService, type Notification } from 'api/services/notificationService';

/**
 * Notificaciones del usuario: las guardadas más las que llegan en vivo.
 *
 * OJO con la conexión: NO llama a createEcho(). Esa conexión la abre
 * useForceLogout una sola vez al entrar, y createEcho() desconecta la
 * anterior cada vez que se llama — si este hook abriera la suya, se tumbarían
 * mutuamente y el cierre forzado de sesión dejaría de funcionar. Aquí solo se
 * toma la que ya existe con getEcho() y se agrega otro listener al MISMO
 * canal privado.
 *
 * Como el hook puede montarse antes de que la conexión exista, se reintenta
 * tomarla un rato en lugar de rendirse al primer intento.
 */
export function useNotifications() {
  const token = useAuthStore((s) => s.token);
  const keyUser = useAuthStore((s) => s.user?.keyUser);

  const [items, setItems] = useState<Notification[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    if (!token) return;
    setLoading(true);
    notificationService.list()
      .then((r) => { setItems(r.items); setUnread(r.unread); })
      .catch(() => { /* la campanita no debe romper la pantalla */ })
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // ── En vivo ──
  useEffect(() => {
    if (!token || !keyUser) return;

    let canal: any = null;
    let intentos = 0;

    const enganchar = () => {
      const echo = getEcho();
      if (!echo) {
        // La conexión la abre useForceLogout; puede tardar un instante.
        if (intentos++ < 20) setTimeout(enganchar, 500);
        return;
      }

      canal = echo.private(`user.${keyUser}`);
      canal.listen('.notification', (n: Notification) => {
        setItems((prev) => [{ ...n, read: false, readAt: null, actorName: null }, ...prev].slice(0, 30));
        setUnread((u) => u + 1);
        toast.info(n.title, { autoClose: 6000 });
      });
    };

    enganchar();

    return () => {
      // Solo se suelta ESTE listener: la conexión y el de force-logout siguen.
      try { canal?.stopListening('.notification'); } catch { /* sin conexión, nada que soltar */ }
    };
  }, [token, keyUser]);

  const marcarLeida = async (key: string) => {
    setItems((prev) => prev.map((n) => (n.keyNotification === key ? { ...n, read: true } : n)));
    setUnread((u) => Math.max(0, u - 1));
    try { await notificationService.markRead(key); } catch { load(); }
  };

  const marcarTodas = async () => {
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnread(0);
    try { await notificationService.markAllRead(); } catch { load(); }
  };

  return { items, unread, loading, load, marcarLeida, marcarTodas };
}
